// The work of an own Sapper (R2657, ui-map-interface.md §6.1.14,
// v2-the-deep.md §11.8): the slot `sapperwork`, right after `gatherover`.
//
// The layer draws the lay look and the collect look of an own Sapper, the
// looks of the World fields `seaMineLay`, `deployableLay`,
// `seaMineCollect` and `deployableCollect` (rounds 071 to 074). The wire
// carries no lay and no collect, so the scene hands the layer the moments
// the store read off the rows (`SeaObjectDelta`): a lay starts a run on
// its Sapper, and a collect plays the take of the rows that came aboard.
// The looks draw the mark of the `seaMine` or the `deployable` field in
// place of the placeholder of the rounds, at the place, the alpha and the
// extent the card gives (`LayScene.mark`, `DeployLay.mark`,
// `CollectScene.mark`, `DeployCollectScene.mark`).
//
// A row a run draws is hidden on the `seaobjects` layer (`drawn`), so no
// object draws twice. The loud lay reads the timer of the own Sapper
// (`FleetState.loud_left_sec`), and the arm reads the row: the arm window
// the server states (`arm_left_map_ms`, R2737), never a guess from the
// distance to the layer.
//
// The hostile view of a lay (R2841, v2-the-deep.md §11.8): the store reads a
// lay of a Sapper contact of another faction off a new row of that faction
// (`SeaObjectDelta.foeLays`). An unarmed hostile sea mine plays the lay look
// in the hostile view (`LayScene.own` false), in the ink of the layer's
// side: the mark, the arm and the bubbles of card 20, and the loud tell of
// the look, but no Loud Clock of card 18, which is own information. While
// the loud lay of the contact runs, a bearing of card 20 of round 072 runs
// from the viewer's nearest listener to the contact (`paintLoudBearing`).
//
// The layer draws the cache work look too (R2833): the look of the World
// field `cacheWork` at the hull of each own fleet that works a SEARCH at
// its point, at the share of the work done. The fleet layer
// states the share (`FleetLayer.ownWorks`), and the look stops on the
// frame the work ends.
//
// Everything paints on one paint sheet (`SheetKeeper`), which paints each
// frame while a look runs and not at all when none runs.

import type { DrawCtx, FrameInfo, Layer } from '../layer';
import { type BatchSource, type SheetSink, sheetOf } from './batches';
import { SheetKeeper, clearBox, emptyBox, growBox, type Box } from './sheetpaint';
import { SeaObjectKind, type SeaObjectStock } from '../../gen/proto/aquaphobia';
import { SEA_OBJECTS } from '../../gen/presentation';
import { factionColor } from '../../world';
import { CHART_MONO_FAMILY } from '../gfx/atlas';
import {
  armShare, armStartMs, isSeaMineKind, seaKindFigures, seaRules, type SeaCollect, type SeaGunHit, type SeaLay,
  type SeaObjectDelta, type SeaObjectRow,
} from '../../seaobjects';
import { GUN_KILL_LOOKS, GUN_KILL_STYLE, type GunKill } from '../../render/gun-kill-looks';
import { SEA_MINE_KIND_OF, type SeaChartReading, type SeaMineStyle } from '../../render/seamine-marks';
import { DEPLOYABLE_KIND_OF, LINE_KINDS, type DeployableStyle } from '../../render/deployable-marks';
import {
  SEA_MINE_LAY_LOOKS, paintDeployableLay, paintLaidMine, paintLayLoud, paintLoudBearing, paintSeaMineLay, seaMineLayLife,
  type DeployLay, type LaidMine, type LaidState, type LayScene, type SeaMineLayStyle,
  type DeployableLayStyle,
} from '../../render/sapper-lay-looks';
import {
  COLLECT_SPAN, SEA_MINE_COLLECT_LOOKS, deployableCollectEnd, paintDeployableCollect,
  paintSeaMineCollect, planDeployableTake, planSeaMineTake,
  type CollectScene, type DeployCollectScene, type DeployableCollectStyle, type FieldItem, type FieldMine,
  type SeaMineCollectStyle, type StowItem,
} from '../../render/sapper-collect-looks';
import {
  SEA_MINE_DETONATION_LOOKS, paintSeaMineDetonation, type SeaMineDetonationStyle, type SeaMineTrigger,
} from '../../render/seamine-detonation-looks';
import { mulberry32, rot, type Pt } from '../../render/seaobject-kit';
import { AUTO_SCENE_LOOK, type SceneLook } from '../../render/scene-look';
import { reducedMotion } from '../../render/motion';
import { paintDeployableMark, paintSeaMineMark, type MarkPaint } from './seaobjects';
import type { FleetInfo } from '../../render/fleet-info';
import type { SonarSource } from '../../render/sonar-types';
import type { OwnWork } from './fleets';
import { cargoSum } from '../../ui/rounds';
import {
  cacheTake, cacheWorkAt, paintCacheWork, type CacheTake, type CacheWorkFigures, type CacheWorkStyle,
} from '../../render/cache-work-looks';

/**
 * The listener of the hostile view of a lay (R2841): the source of the
 * viewer nearest to `x, y`, from the fixed listeners the Empire frame lists
 * (`sonar_rings`) and the own hulls that listen. The server judges who
 * hears; the bearing names the nearest ear. Null when the viewer has none.
 */
export function nearestListener(rings: readonly SonarSource[], fleets: readonly FleetInfo[], x: number, y: number): Pt | null {
  let best: Pt | null = null;
  let bd = Infinity;
  for (const r of rings) {
    if (!(r.range > 0)) continue;
    const d = Math.hypot(r.x - x, r.y - y);
    if (d < bd) { bd = d; best = [r.x, r.y]; }
  }
  for (const f of fleets) {
    if (!f.own || !(f.sonarPx > 0)) continue;
    const d = Math.hypot(f.x - x, f.y - y);
    if (d < bd) { bd = d; best = [f.x, f.y]; }
  }
  return best;
}

/** Where an own Sapper stands now, as the fleet layer draws it. */
export interface SapperPose { x: number; y: number; head: number }

/** Where the layer reads the world. */
export interface SapperWorkFeeds {
  /** The pose of an own hull, or null when the fleet layer lacks it. */
  pose(id: number): SapperPose | null;
  /** The row of an id, or undefined once it left the picture. */
  row(id: number): SeaObjectRow | undefined;
  /** The map seconds the loud lay of an own Sapper still runs. */
  loudLeftSec(id: number, nowMs: number): number;
  /** The map seconds the loud lay of a Sapper contact of another faction
   *  still runs, from the lay the store saw (R2841). */
  foeLoudLeftSec(id: number, nowMs: number): number;
  /** The viewer's listener nearest to a point (`nearestListener`), or null. */
  listener(x: number, y: number): Pt | null;
  /** The cargo room of an own Sapper, in the cargo units of the stow. */
  cargoCap(id: number): number;
  /** Map seconds per wall second. */
  mapPerWall(): number;
  /** The listening range of a kind from the catalog, px; 0 for none. */
  sonarPx(kind: SeaObjectKind): number;
  /** Every own fleet that works a SEARCH at its point now
   *  (R2833). The list is the fleet layer's, read inside the frame. */
  works(): readonly OwnWork[];
}

/** The figures of the cache work look (`[presentation.seaobjects]`). */
const CACHE_WORK: CacheWorkFigures = {
  rounds: SEA_OBJECTS.cacheWorkRounds, pieces: SEA_OBJECTS.cacheWorkPieces,
  R: SEA_OBJECTS.cacheWorkPx, cap: SEA_OBJECTS.cacheWorkCap,
};

/** The take a cache work plays now, kept while its look and its round
 *  hold, because a look plans its schedule in many steps. */
interface WorkTake { style: CacheWorkStyle; take: CacheTake }

/** One sea mine of a lay run. */
interface RunMine {
  id: number;
  i: number;
  kind: SeaObjectKind;
  atMs: number;
  lx: number;
  ly: number;
}

/** The sea mine lays of one own Sapper, or of a Sapper contact of another
 *  faction (`foe`, R2841). */
interface MineRun {
  sapper: number;
  foe: boolean;
  /** The faction of the layer: its ink. */
  faction: number;
  mines: RunMine[];
  lastMs: number;
  lastI: number;
}

/** The lay of one deployable. */
interface DeployRun {
  id: number;
  sapper: number;
  kind: SeaObjectKind;
  atMs: number;
}

/** One collect that plays. */
interface CollectRun {
  sapper: number;
  x: number;
  y: number;
  head: number;
  atMs: number;
  rows: readonly SeaObjectRow[];
  stow0: readonly SeaObjectStock[];
  cap: number;
}

/** One detonation that plays. */
interface Burst {
  x: number;
  y: number;
  kind: SeaObjectKind;
  faction: number;
  hit: Pt | null;
  trigger: SeaMineTrigger;
  /** The wall ms of the blast: for a gun hit, the arrival of the first
   *  round of the salvo (R3031). */
  atMs: number;
  seed: number;
  /** The gun kill of a sea mine a gun hit set off (R3032): the unit
   *  bearing of the shot and the seeded spread of the look; null for a
   *  hull, a round, rock and a link of a chain. */
  kill: { ux: number; uy: number; spread: Float64Array } | null;
}

/** The one gun kill a frame paints, rewritten in place (§10). */
const KILL: GunKill = { x: 0, y: 0, ux: 0, uy: 0, R: 0, spread: new Float64Array(0), z: 1 };

/** The cargo of a stow, in the cargo units of the pack. */
export function stowCargo(stow: readonly SeaObjectStock[]): number {
  return cargoSum(stow, seaCargo);
}

/** The cargo of one item of a sea-object kind, 0 for a kind the pack does not name. */
function seaCargo(kind: SeaObjectKind): number {
  return seaKindFigures(kind)?.cargo ?? 0;
}

/** The stow items of the sea mine kinds, one item a sea mine, in kind
 *  order: the stow display of a sea mine collect. */
export function stowItems(stow: readonly SeaObjectStock[]): StowItem[] {
  const out: StowItem[] = [];
  for (const s of [...stow].sort((a, b) => a.kind - b.kind)) {
    const kind = SEA_MINE_KIND_OF.get(s.kind), f = seaKindFigures(s.kind);
    if (!kind || !f) continue;
    for (let i = 0; i < s.count; i++) out.push({ kind, cargo: f.cargo });
  }
  return out;
}

/** The anchor time of a deployable kind, map seconds. */
function anchorSec(kind: SeaObjectKind): number {
  return kind === SeaObjectKind.SEA_OBJECT_KIND_PALISADE ? seaRules().palisadeAnchorSec : seaRules().anchorSec;
}

export class SapperWorkLayer implements Layer {
  readonly name = 'sapperwork';
  private shown = true;
  private readonly sheet: SheetSink;
  private readonly keeper: SheetKeeper;
  private look: SceneLook = AUTO_SCENE_LOOK;
  private reading: SeaChartReading = 'off';
  private motion = !reducedMotion();
  private viewer = 0;
  private readonly mineRuns = new Map<number, MineRun>();
  private readonly deployRuns: DeployRun[] = [];
  private readonly collects: CollectRun[] = [];
  private readonly bursts: Burst[] = [];
  private filming = false;
  private burstSeed = 1;
  private readonly drawnV = new Set<number>();
  /** The Sapper contacts of another faction whose loud lay runs (R2841). */
  private readonly foeLoud = new Set<number>();
  /** The own works of this frame, and the take of each fleet (R2833). */
  private works: readonly OwnWork[] = [];
  private readonly takes = new Map<number, WorkTake>();
  private readonly box: Box = emptyBox();
  private nowMs = 0;
  private timeS = 0;

  constructor(src: BatchSource, private readonly feeds: SapperWorkFeeds) {
    this.sheet = sheetOf(src);
    this.keeper = new SheetKeeper(this.sheet);
  }

  /** The ids a look draws now: the `seaobjects` layer hides them. */
  get drawn(): ReadonlySet<number> { return this.drawnV; }

  /** How many looks run now. The tests read it. */
  get running(): number {
    return this.mineRuns.size + this.deployRuns.length + this.collects.length + this.bursts.length
      + this.works.length + this.foeLoud.size;
  }

  /** The four work fields and the two mark fields of the look. */
  setLook(l: SceneLook): void { this.look = l; this.motion = !reducedMotion(); }

  /** The chart reading the AUTO sea mine mark follows. */
  setReading(r: SeaChartReading): void { this.reading = r; }

  /** The viewer's faction: an own look draws in its ink. */
  setViewer(faction: number): void { this.viewer = faction; }

  /** Takes every look off the water: a hello, a film. */
  clear(): void {
    this.mineRuns.clear();
    this.deployRuns.length = 0;
    this.collects.length = 0;
    this.bursts.length = 0;
    this.drawnV.clear();
    this.foeLoud.clear();
    this.works = [];
    this.takes.clear();
  }

  /**
   * A replay film plays on the map (R531): the layer drops the looks of
   * today, takes no moment of the live frames, and plays the detonations
   * the film hands it (`burst`). The scene writes the flag every frame.
   */
  setFilming(on: boolean): void {
    if (on === this.filming) return;
    this.filming = on;
    this.clear();
  }

  /** The moments of one State frame (`SeaObjectStore.applyState`). */
  applyDelta(d: SeaObjectDelta | null, nowMs: number): void {
    if (!d || this.filming) return;
    for (const l of d.lays) this.lay(l, nowMs, false);
    for (const l of d.foeLays) {
      this.foeLoud.add(l.sapper);
      if (isSeaMineKind(l.row.kind)) this.lay(l, nowMs, true);
    }
    for (const c of d.collects) this.collect(c);
    for (const x of d.detonations) {
      this.burst(x.row.x, x.row.y, x.row.kind, x.row.faction, x.hit ? [x.hit.x, x.hit.y] : null, nowMs, x.gun);
    }
    this.rebuildDrawn();
  }

  /**
   * One sea mine went off at `x, y` (§11.2): the look of the
   * `seaMineDetonation` field, in the ink of the side that laid it. A hull
   * set it off at `hit`; with no hull a round did, and the look draws no
   * slow tell (card 20). A gun hit (`gun`, R3031) waits for the first round
   * of the salvo the combat layer flies, and then the gun kill look plays
   * over the burst (R3032).
   */
  burst(
    x: number, y: number, kind: SeaObjectKind, faction: number, hit: Pt | null, nowMs = Date.now(), gun?: SeaGunHit,
  ): void {
    if (!SEA_MINE_KIND_OF.has(kind)) return;
    const seed = 7919 * this.burstSeed++;
    let kill: Burst['kill'] = null;
    const dx = gun ? x - gun.x : 0, dy = gun ? y - gun.y : 0, n = Math.hypot(dx, dy);
    if (gun && n > 0) {
      const look = GUN_KILL_LOOKS[GUN_KILL_STYLE];
      const spread = new Float64Array(look.spreadLen);
      look.spread(mulberry32(seed), spread);
      kill = { ux: dx / n, uy: dy / n, spread };
    }
    this.bursts.push({
      x, y, kind, faction, hit, trigger: hit ? 'hull' : 'round',
      atMs: nowMs + (gun ? gun.waitSec * 1000 : 0), seed, kill,
    });
  }

  private lay(l: SeaLay, nowMs: number, foe: boolean): void {
    const r = l.row;
    if (isSeaMineKind(r.kind)) {
      let run = this.mineRuns.get(l.sapper);
      if (!run) { run = { sapper: l.sapper, foe, faction: r.faction, mines: [], lastMs: nowMs, lastI: -1 }; this.mineRuns.set(l.sapper, run); }
      run.lastI++;
      run.lastMs = nowMs;
      run.mines.push({ id: r.id, i: run.lastI, kind: r.kind, atMs: nowMs, lx: r.x, ly: r.y });
      return;
    }
    if (DEPLOYABLE_KIND_OF.has(r.kind)) this.deployRuns.push({ id: r.id, sapper: l.sapper, kind: r.kind, atMs: nowMs });
  }

  private collect(c: SeaCollect): void {
    const p = this.feeds.pose(c.sapper);
    if (!p) return;
    this.collects.push({
      sapper: c.sapper, x: p.x, y: p.y, head: p.head, atMs: c.atMs, rows: c.rows, stow0: c.stow0,
      cap: this.feeds.cargoCap(c.sapper),
    });
  }

  private rebuildDrawn(): void {
    this.drawnV.clear();
    for (const run of this.mineRuns.values()) for (const m of run.mines) this.drawnV.add(m.id);
    for (const d of this.deployRuns) this.drawnV.add(d.id);
  }

  update(frame: FrameInfo): void {
    this.nowMs = Date.now();
    this.timeS = frame.timeS;
    this.prune();
    const box = clearBox(this.box);
    for (const run of this.mineRuns.values()) {
      const p = this.feeds.pose(run.sapper);
      if (p) growBox(box, p.x, p.y, 40);
      for (const m of run.mines) {
        const r = this.feeds.row(m.id);
        if (r) growBox(box, r.x, r.y, 30);
      }
    }
    for (const id of this.foeLoud) {
      const p = this.feeds.pose(id), h = p ? this.feeds.listener(p.x, p.y) : null;
      if (p) growBox(box, p.x, p.y, 4);
      if (h) growBox(box, h[0], h[1], 4);
    }
    for (const d of this.deployRuns) {
      const r = this.feeds.row(d.id), p = this.feeds.pose(d.sapper);
      const f = seaKindFigures(d.kind);
      if (r) growBox(box, r.x, r.y, Math.max(f?.lengthPx ?? 0, this.feeds.sonarPx(d.kind), 20) + 70);
      if (p) growBox(box, p.x, p.y, 70);
    }
    for (const c of this.collects) growBox(box, c.x, c.y, seaRules().collectPx + 60);
    for (const b of this.bursts) growBox(box, b.x, b.y, 60);
    // The cache works (R2833): none while a film plays.
    this.works = this.filming ? [] : this.feeds.works();
    for (const w of this.works) growBox(box, w.x, w.y, CACHE_WORK.R + 40);
    if (this.takes.size > this.works.length) {
      for (const id of this.takes.keys()) if (!this.works.some((w) => w.id === id)) this.takes.delete(id);
    }
    // A running look moves with the clock and paints every frame, and no
    // look paints nothing, so the keeper needs no key.
    const any = this.running > 0;
    this.keeper.frame(any ? box : null, frame.cull, frame.view, frame.cam, '', any, this.paintSheet);
  }

  /** Drops every run whose look is over. */
  private prune(): void {
    const now = this.nowMs, mpw = this.feeds.mapPerWall() || 1;
    let changed = false;
    for (const [id, run] of this.mineRuns) {
      const keep = run.mines.filter((m) => {
        const r = this.feeds.row(m.id);
        if (!r) return false;
        const age = (now - m.atMs) / 1000;
        // A running arm window keeps the sea mine to its arm, waits a little
        // past the end for the armed flag of the next frame, and then plays
        // the armed flash (R2737).
        const hold = SEA_OBJECTS.armHoldSec * 1000;
        const end = armStartMs(r, mpw) + (seaRules().armAfterSec * 1000) / mpw + hold;
        const holding = r.armed ? r.armedMs > 0 && now - r.armedMs <= hold : r.armLeftMs > 0 && now < end;
        if (age > SEA_OBJECTS.layLookMaxSec && !holding) return false;
        return !(r.armed && r.armedMs > 0 && (now - r.armedMs) / 1000 > SEA_OBJECTS.armHoldSec);
      });
      if (keep.length !== run.mines.length) changed = true;
      run.mines = keep;
      if (keep.length === 0 && this.loudLeft(run.sapper, run.foe, now) <= 0) { this.mineRuns.delete(id); changed = true; }
    }
    for (const id of this.foeLoud) if (this.feeds.foeLoudLeftSec(id, now) <= 0) this.foeLoud.delete(id);
    const loudLook = seaRules().layNoiseSec / mpw;
    for (let i = this.deployRuns.length - 1; i >= 0; i--) {
      const d = this.deployRuns[i], r = this.feeds.row(d.id);
      const age = (now - d.atMs) / 1000;
      const dur = Math.max(anchorSec(d.kind) / mpw, SEA_OBJECTS.anchorLookMinSec);
      if (!r || age > SEA_OBJECTS.layLookMaxSec || age > Math.max(dur + 1.5, Math.min(loudLook, SEA_OBJECTS.layLookMaxSec))) {
        this.deployRuns.splice(i, 1); changed = true;
      }
    }
    for (let i = this.collects.length - 1; i >= 0; i--) {
      const c = this.collects[i];
      const end = Math.max(COLLECT_SPAN, SEA_OBJECTS.collectLeadSec + SEA_OBJECTS.collectWindowSec) + 1;
      if ((now - c.atMs) / 1000 > end) { this.collects.splice(i, 1); changed = true; }
    }
    const life = Math.max(
      SEA_MINE_DETONATION_LOOKS[this.look.seaMineDetonation as SeaMineDetonationStyle].life,
      GUN_KILL_LOOKS[GUN_KILL_STYLE].life,
    );
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      if ((now - this.bursts[i].atMs) / 1000 > life) this.bursts.splice(i, 1);
    }
    if (changed) this.rebuildDrawn();
  }

  /** The map seconds the loud lay of a run's Sapper still runs. */
  private loudLeft(sapper: number, foe: boolean, now: number): number {
    return foe ? this.feeds.foeLoudLeftSec(sapper, now) : this.feeds.loudLeftSec(sapper, now);
  }

  /** The paint of the sheet, bound once, so a frame allocates no closure. */
  private readonly paintSheet = (c: CanvasRenderingContext2D, z: number): void => this.paint(c, z);

  private markPaint(z: number): MarkPaint {
    return { z, t: this.timeS, motion: this.motion, reading: this.reading };
  }

  private paint(c: CanvasRenderingContext2D, z: number): void {
    const face = CHART_MONO_FAMILY;
    for (const run of this.mineRuns.values()) this.paintMineRun(c, run, z, face);
    for (const d of this.deployRuns) this.paintDeployRun(c, d, z, face);
    for (const k of this.collects) this.paintCollect(c, k, z, face);
    for (const b of this.bursts) this.paintBurst(c, b, z);
    for (const w of this.works) this.paintWork(c, w, z, face);
    for (const id of this.foeLoud) this.paintFoeBearing(c, id, z);
  }

  // -- the bearing to a loud hostile Sapper (R2841) ---------------------------

  private paintFoeBearing(c: CanvasRenderingContext2D, id: number, z: number): void {
    const p = this.feeds.pose(id);
    const h = p ? this.feeds.listener(p.x, p.y) : null;
    if (!p || !h) return;
    paintLoudBearing(c, h, [p.x, p.y], factionColor(this.viewer), z);
  }

  // -- the cache work (R2833) ------------------------------------------------

  private paintWork(c: CanvasRenderingContext2D, w: OwnWork, z: number, face: string): void {
    const style = this.look.cacheWork as CacheWorkStyle;
    const at = cacheWorkAt(w.share, CACHE_WORK);
    let t = this.takes.get(w.id);
    if (!t || t.style !== style || t.take.round !== at.round) {
      t = { style, take: cacheTake(style, at.round, w.id, CACHE_WORK) };
      this.takes.set(w.id, t);
    }
    c.save(); c.translate(w.x, w.y); c.rotate(w.head);
    paintCacheWork(c, style, t.take, CACHE_WORK.R, {
      u: at.u, still: !this.motion, ownCol: factionColor(this.viewer), z, face, head: w.head,
      cap: CACHE_WORK.cap, timer: w.wallLeft,
    });
    c.restore();
  }

  // -- the detonation (round 070) --------------------------------------------

  private paintBurst(c: CanvasRenderingContext2D, b: Burst, z: number): void {
    // A gun hit waits for its first round: nothing paints before it lands.
    const k = (this.nowMs - b.atMs) / 1000;
    if (k < 0) return;
    const kind = SEA_MINE_KIND_OF.get(b.kind)!;
    const i = b.kind - SeaObjectKind.SEA_OBJECT_KIND_BARB;
    const style = this.look.seaMineDetonation as SeaMineDetonationStyle;
    if (k < SEA_MINE_DETONATION_LOOKS[style].life) {
      paintSeaMineDetonation(c, style, {
        x: b.x, y: b.y, R: SEA_OBJECTS.burstPx[i], w: SEA_OBJECTS.burstWeight[i], kind, col: factionColor(b.faction),
        i: 0, rnd: mulberry32(b.seed), hit: b.hit, trigger: b.trigger, z,
      }, k);
    }
    if (!b.kill) return;
    // The gun kill look over the burst, on the struck sea mine (R3032).
    KILL.x = b.x; KILL.y = b.y; KILL.ux = b.kill.ux; KILL.uy = b.kill.uy;
    KILL.R = SEA_OBJECTS.burstPx[i]; KILL.spread = b.kill.spread; KILL.z = z;
    GUN_KILL_LOOKS[GUN_KILL_STYLE].paint(c, KILL, k);
  }

  // -- the sea mine lay (round 071) -----------------------------------------

  private paintMineRun(c: CanvasRenderingContext2D, run: MineRun, z: number, face: string): void {
    const p = this.feeds.pose(run.sapper);
    if (!p) return;
    const now = this.nowMs, style = this.look.seaMineLay as SeaMineLayStyle, mpw = this.feeds.mapPerWall() || 1;
    const mineStyle = this.look.seaMine as SeaMineStyle;
    // A hostile run draws in the ink of the layer's side (R2841).
    const col = factionColor(run.foe ? run.faction : this.viewer), mp = this.markPaint(z);
    const loudLeft = this.loudLeft(run.sapper, run.foe, now);
    const mines: LaidMine[] = [];
    for (const m of run.mines) {
      const r = this.feeds.row(m.id);
      if (!r) continue;
      const d = Math.hypot(r.x - p.x, r.y - p.y);
      const state: LaidState = r.armed ? 'armed' : r.inert || r.disarmedMs > 0 ? 'inert' : 'unarmed';
      const from = armStartMs(r, mpw);
      const f = seaKindFigures(r.kind);
      mines.push({
        i: m.i, x: r.x, y: r.y, lx: m.lx, ly: m.ly, wx: m.lx, wy: m.ly, dvx: 0, dvy: 0,
        r: r.kind === SeaObjectKind.SEA_OBJECT_KIND_PETARD ? 4 : 3, reach: f?.triggerPx ?? 0,
        col: factionColor(r.faction), k: (now - m.atMs) / 1000, d, state,
        armU: state === 'inert' ? -1 : armShare(r, now, mpw),
        kArm: r.armed ? (r.armedMs > 0 ? (now - r.armedMs) / 1000 : 99) : -1,
        kFar: from > 0 ? (now - from) / 1000 : -1,
        kIn: r.disarmedMs > 0 ? (now - r.disarmedMs) / 1000 : -1,
        dis: r.disarmedMs > 0, seed: m.id, kind: SEA_MINE_KIND_OF.get(r.kind),
      });
    }
    const o: LayScene = {
      own: !run.foe, col, H: { x: p.x, y: p.y, hdg: p.head, s: 1 }, mines,
      kLast: (now - run.lastMs) / 1000, loud: loudLeft > 0,
      loudU: Math.max(0, Math.min(1, 1 - loudLeft / seaRules().layNoiseSec)), loudA: loudLeft > 0 ? 1 : 0,
      lastI: run.lastI, z, armPx: seaRules().armClearPx, loudMul: seaRules().layNoiseMul,
      cur: [0, 0], curV: 0, face, armTurns: this.motion ? SEA_OBJECTS.armSpinTurns : 0,
      mark: (cc, m, x, y, a, s) => {
        const kind = run.mines.find((q) => q.i === m.i)?.kind ?? SeaObjectKind.SEA_OBJECT_KIND_BARB;
        paintSeaMineMark(cc, mineStyle, kind, x, y, m.col, m.state === 'armed', false, a, s, (m.seed % 97) / 97, mp);
      },
    };
    const look = SEA_MINE_LAY_LOOKS[style];
    look.under?.(c, o);
    if (look.loudUnder && o.loud) paintLayLoud(c, style, o.H, o);
    const life = seaMineLayLife(style);
    for (const m of mines) if (m.k < life && look.layUnder) paintSeaMineLay(c, style, m, o);
    for (const m of mines) paintLaidMine(c, style, m, o);
    for (const m of mines) if (m.k < life && !look.layUnder) paintSeaMineLay(c, style, m, o);
    if (look.hull) { c.save(); c.translate(p.x, p.y); c.rotate(p.head); look.hull(c, o); c.restore(); }
    if (!look.loudUnder && o.loud) paintLayLoud(c, style, o.H, o);
  }

  // -- the deployable lay (round 072) -------------------------------------------

  private paintDeployRun(c: CanvasRenderingContext2D, d: DeployRun, z: number, face: string): void {
    const r = this.feeds.row(d.id), p = this.feeds.pose(d.sapper);
    const kind = DEPLOYABLE_KIND_OF.get(d.kind);
    if (!r || !kind) return;
    const now = this.nowMs, mpw = this.feeds.mapPerWall() || 1;
    const f = seaKindFigures(d.kind);
    const dur = Math.max(anchorSec(d.kind) / mpw, SEA_OBJECTS.anchorLookMinSec);
    const t = (now - d.atMs) / 1000;
    const tS = r.armed && r.armedMs > 0 ? Math.max(0, (r.armedMs - d.atMs) / 1000) : dur;
    const col = factionColor(r.faction), mp = this.markPaint(z);
    const depStyle = this.look.deployable as DeployableStyle;
    const line = LINE_KINDS.has(kind);
    // The frame of the look: the deployable at the origin, a line along y.
    const turn = line ? r.heading : 0;
    const S: Pt = p ? rot([p.x - r.x, p.y - r.y], -turn) : [0, 0];
    const loudLeft = this.feeds.loudLeftSec(d.sapper, now);
    const o: DeployLay = {
      kind, L: f?.lengthPx ?? 0, Rc: f?.lengthPx ?? 0, heavy: d.kind === SeaObjectKind.SEA_OBJECT_KIND_PALISADE,
      t, tL: 0, tS, dur, u: Math.min(1, t / dur), k: r.armed ? t - tS : -1,
      laid: true, live: r.armed, loud: loudLeft > 0, loudSec: seaRules().layNoiseSec / mpw,
      col, own: true, S: { x: S[0], y: S[1], hdg: p ? p.head - turn : 0 }, listener: null,
      viewerCol: factionColor(this.viewer), z, face,
      mark: (cc, ink, a) => paintDeployableMark(cc, depStyle, d.kind, ink, r.hullPct / 100, a,
        line ? r.heading + Math.PI / 2 : 0, this.feeds.sonarPx(d.kind), null, mp),
    };
    c.save(); c.translate(r.x, r.y); if (turn) c.rotate(turn);
    paintDeployableLay(c, this.look.deployableLay as DeployableLayStyle, o);
    c.restore();
  }

  // -- the collects (rounds 073 and 074) -------------------------------------

  private paintCollect(c: CanvasRenderingContext2D, k: CollectRun, z: number, face: string): void {
    const now = this.nowMs, t = (now - k.atMs) / 1000;
    const mp = this.markPaint(z);
    const ownCol = factionColor(this.viewer);
    const foe = k.rows.find((r) => r.faction !== this.viewer);
    const foeCol = foe ? factionColor(foe.faction) : ownCol;
    // The frame of the look: the Sapper at rest at the origin, bow to +x.
    const local = (r: SeaObjectRow): Pt => rot([r.x - k.x, r.y - k.y], -k.head);
    c.save(); c.translate(k.x, k.y); c.rotate(k.head);
    const mines = k.rows.filter((r) => isSeaMineKind(r.kind));
    if (mines.length > 0) {
      const field: FieldMine[] = mines.map((r) => {
        const [x, y] = local(r);
        return {
          id: r.id, kind: SEA_MINE_KIND_OF.get(r.kind)!, side: r.faction === this.viewer ? 'own' : 'foe',
          d: Math.hypot(x, y), a: Math.atan2(y, x), x, y, cargo: seaKindFigures(r.kind)?.cargo ?? 0,
        };
      });
      const kindOf = new Map(mines.map((r) => [r.id, r.kind] as const));
      const style = this.look.seaMineCollect as SeaMineCollectStyle, mineStyle = this.look.seaMine as SeaMineStyle;
      const R = Math.max(seaRules().collectPx, ...field.map((m) => m.d));
      const P = planSeaMineTake(field, R, stowCargo(k.stow0), Math.max(k.cap, stowCargo(k.stow0) + 1));
      const X: CollectScene = {
        k: t, R, P, Q: SEA_MINE_COLLECT_LOOKS[style].prep(P, R), stow0: stowItems(k.stow0),
        cap: Math.max(1, k.cap), ownCol, foeCol, z, face, head: k.head,
        mark: (cc, m, x, y, q, a, s) => paintSeaMineMark(cc, mineStyle, kindOf.get(m.id) ?? SeaObjectKind.SEA_OBJECT_KIND_BARB,
          x, y, q < 0.5 && m.side === 'foe' ? foeCol : ownCol, q < 0.5, m.side === 'foe' && q < 0.5, a, s, (m.id % 97) / 97, mp),
      };
      paintSeaMineCollect(c, style, X);
    }
    const deps = k.rows.filter((r) => DEPLOYABLE_KIND_OF.has(r.kind));
    if (deps.length > 0) {
      const items: FieldItem[] = deps.map((r) => {
        const [x, y] = local(r);
        return {
          id: String(r.id), kind: DEPLOYABLE_KIND_OF.get(r.kind)!, side: r.faction === this.viewer ? 'own' : 'foe',
          x, y, ang: r.heading + Math.PI / 2 - k.head, cargo: seaKindFigures(r.kind)?.cargo ?? 0,
        };
      });
      const kindOf = new Map(deps.map((r) => [String(r.id), r] as const));
      const style = this.look.deployableCollect as DeployableCollectStyle;
      const depStyle = this.look.deployable as DeployableStyle;
      const R = Math.max(seaRules().collectPx, ...items.map((it) => Math.hypot(it.x, it.y)));
      const cap = Math.max(k.cap, stowCargo(k.stow0) + 1);
      const p = planDeployableTake(items, R, stowCargo(k.stow0), cap);
      const seine = seaKindFigures(SeaObjectKind.SEA_OBJECT_KIND_SEINE)?.lengthPx ?? 0;
      const palisade = seaKindFigures(SeaObjectKind.SEA_OBJECT_KIND_PALISADE)?.lengthPx ?? 0;
      const rc = seaKindFigures(SeaObjectKind.SEA_OBJECT_KIND_CHEVAL)?.lengthPx ?? 0;
      const base = {
        t, R, p, own: true, tStop: 0, tC0: SEA_OBJECTS.collectLeadSec,
        tC1: SEA_OBJECTS.collectLeadSec + SEA_OBJECTS.collectWindowSec, sp: [-SEA_OBJECTS.stowAftPx, 0] as Pt,
        lenSeine: seine, lenPalisade: palisade, Rc: rc, cap, ownCol, foeCol, z, face, head: k.head,
        mark: (cc: CanvasRenderingContext2D, it: FieldItem, ink: number, a: number) => {
          const r = kindOf.get(it.id);
          if (r) paintDeployableMark(cc, depStyle, r.kind, ink, r.hullPct / 100, a, 0, 0, null, mp);
        },
      };
      const o: DeployCollectScene = { ...base, tEnd: deployableCollectEnd(style, base) };
      paintDeployableCollect(c, style, o);
    }
    c.restore();
  }

  draw(ctx: DrawCtx): void {
    if (!this.shown) return;
    this.sheet.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
